import type { FC, ReactNode } from 'react'

import { ArrowRight, Check } from 'lucide-react'
import HeroReplay from '@/components/landing/hero-replay'
import NeonLink from '@/components/landing/neon-link'

const TRUST_POINTS = ['Free forever', 'No ads, ever', 'Runs in any browser']

const HeroSection: FC = (): ReactNode => {
    return (
        <section id='hero' aria-labelledby='hero-title' className='relative isolate overflow-hidden pb-20 pt-28 sm:pt-32 lg:pb-28 lg:pt-36'>
            <div aria-hidden='true' className='pointer-events-none absolute inset-0 -z-10'>
                <div className='absolute -left-48 top-0 size-[36rem] rounded-full bg-neon-pink/20 blur-[130px]' />
                <div className='absolute -right-40 top-32 size-[32rem] rounded-full bg-neon-cyan/15 blur-[130px]' />
                <div className='absolute left-1/3 top-2/3 size-[24rem] rounded-full bg-neon-violet/15 blur-[120px]' />

                <div className='absolute inset-x-0 bottom-0 h-[52%] [perspective:640px]'>
                    <div className='neon-grid-bg animate-neon-grid absolute inset-x-[-50%] bottom-[-8%] h-full origin-bottom [mask-image:linear-gradient(to_top,black_8%,transparent_80%)] [transform:rotateX(64deg)]' />
                </div>
            </div>

            <div className='mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-[1.05fr_1fr] lg:gap-12'>
                <div className='relative max-w-2xl'>
                    <p className='inline-flex items-center gap-2.5 rounded-full border border-neon-pink/40 bg-neon-pink/10 px-3.5 py-1.5 font-mono text-[11px] font-semibold uppercase tracking-[0.22em] text-neon-pink'>
                        <span className='relative flex size-2'>
                            <span className='absolute inset-0 rounded-full bg-neon-pink animate-neon-ping' />
                            <span className='relative size-2 rounded-full bg-neon-pink' />
                        </span>
                        Open beta · Season 01
                    </p>

                    <h1 id='hero-title' className='mt-7 font-heading text-[2.4rem] font-semibold leading-[1.05] tracking-tight text-balance sm:text-6xl lg:text-[4.1rem]'>
                        Chess that <span className='text-neon-cyan glow-text-cyan animate-neon-flicker'>glows</span> in the dark.
                    </h1>

                    <p className='mt-6 max-w-xl text-lg leading-relaxed text-pretty text-muted-foreground'>
                        Play bullet, blitz and rapid against players around the world on boards built for late nights. Solve puzzles, climb nightly arenas and review every game the moment it ends.
                    </p>

                    <div className='mt-9 flex flex-col gap-3 sm:flex-row'>
                        <NeonLink href='#play' size='lg'>
                            Play now
                            <ArrowRight className='size-5' />
                        </NeonLink>

                        <NeonLink href='#puzzle' size='lg' variant='outline'>
                            Try the puzzle
                        </NeonLink>
                    </div>

                    <ul className='mt-9 flex flex-wrap gap-x-6 gap-y-3 text-sm text-muted-foreground'>
                        {TRUST_POINTS.map(point => (
                            <li key={point} className='flex items-center gap-2'>
                                <Check className='size-4 text-neon-lime' />
                                {point}
                            </li>
                        ))}
                    </ul>
                </div>

                <div className='relative mx-auto w-full max-w-[34rem] lg:mr-0'>
                    <HeroReplay />
                </div>
            </div>
        </section>
    )
}

export default HeroSection
